/**
 * Raw telemetry_logs browser for the current tenant.
 * Lands here from the "Telemetry Rows" KPI on OpsDashboard.
 * Shows every column of every row for this tenant, paginated.
 * Supports filters (device_uid, event_type, date range), sortable columns,
 * and CSV export of the current filtered view.
 */
import { useState, useEffect, useCallback } from 'react';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw, ChevronLeft, ChevronRight, Loader2, Database,
  ChevronUp, ChevronDown, Download,
} from 'lucide-react';

const PER_PAGE_OPTIONS = [25, 50, 100, 200, 500];
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// null = header not sortable
const COLUMNS = [
  { key: 'id',            label: 'id',                sortable: true,  align: 'left'  },
  { key: 'device_uid',    label: 'device_uid',        sortable: true,  align: 'left'  },
  { key: null,            label: 'work_order_id',     sortable: false, align: 'left'  },
  { key: 'recorded_at',   label: 'recorded_at (UTC)', sortable: true,  align: 'left'  },
  { key: 'latitude',      label: 'lat',               sortable: true,  align: 'right' },
  { key: 'longitude',     label: 'lng',               sortable: true,  align: 'right' },
  { key: 'temperature',   label: 'temp °C',           sortable: true,  align: 'right' },
  { key: 'humidity',      label: 'humidity %',        sortable: true,  align: 'right' },
  { key: 'weight',        label: 'weight',            sortable: true,  align: 'right' },
  { key: 'battery_level', label: 'battery',           sortable: true,  align: 'right' },
  { key: 'speed',         label: 'speed',             sortable: true,  align: 'right' },
  { key: 'event_type',    label: 'event',             sortable: true,  align: 'left'  },
  { key: null,            label: 'bag_open',          sortable: false, align: 'left'  },
  { key: 'created_at',    label: 'created_at (UTC)',  sortable: true,  align: 'left'  },
];

export default function OpsTelemetryLogsPage() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // filters
  const [deviceUid, setDeviceUid] = useState('');
  const [deviceUidInput, setDeviceUidInput] = useState('');
  const [eventType, setEventType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // sort
  const [sortKey, setSortKey] = useState('recorded_at');
  const [order, setOrder]     = useState('desc');

  const activeParams = useCallback(() => {
    const p = { page, per_page: perPage, sort: sortKey, order };
    if (deviceUid) p.device_uid = deviceUid;
    if (eventType) p.event_type = eventType;
    if (fromDate)  p.from = fromDate;
    if (toDate)    p.to   = toDate;
    return p;
  }, [page, perPage, sortKey, order, deviceUid, eventType, fromDate, toDate]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await opsApi.getTelemetryLogs(activeParams());
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setLastPage(res.data.last_page || 1);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || 'Failed to load telemetry');
    } finally {
      setLoading(false);
    }
  }, [activeParams]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const applyFilter = () => {
    setPage(1);
    setDeviceUid(deviceUidInput.trim());
  };

  const clearFilters = () => {
    setDeviceUidInput('');
    setDeviceUid('');
    setEventType('');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setOrder('desc');
    }
    setPage(1);
  };

  const exportCsv = () => {
    // Reuse the same filter+sort params; page/per_page irrelevant for the full export.
    const { page: _p, per_page: _pp, ...csvParams } = activeParams();
    const qs = new URLSearchParams(csvParams).toString();
    // window.open sends the HTTPOnly auth cookie in a same-site context, same
    // pattern used by exportReportCSV elsewhere in the app.
    window.open(`${API_BASE}/api/v1/ops/telemetry/logs.csv?${qs}`, '_blank');
  };

  const fmt = (v, places) => (v == null ? '—' : (places != null ? Number(v).toFixed(places) : String(v)));
  const fmtTs = (v) => (v ? new Date(v).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '—');

  const hasFilters = !!(deviceUid || eventType || fromDate || toDate);

  return (
    <div className="p-4 sm:p-6 space-y-4 min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="ops-telemetry-logs">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" /> Telemetry Logs
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Raw <code className="text-xs">telemetry_logs</code> rows for this tenant · {total.toLocaleString()} total
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={loading || total === 0} data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading} data-testid="refresh-telemetry">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-2 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl p-3">
        <Input
          placeholder="Filter by device_uid (e.g. TRK-MPN26EJ080515060P)"
          value={deviceUidInput}
          onChange={(e) => setDeviceUidInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
          className="lg:max-w-md"
          data-testid="filter-device-uid"
        />
        <select
          value={eventType}
          onChange={(e) => { setEventType(e.target.value); setPage(1); }}
          className="h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
          data-testid="filter-event-type"
        >
          <option value="">All events</option>
          <option value="reading">reading</option>
          <option value="bag_open">bag_open</option>
          <option value="bag_close">bag_close</option>
          <option value="pickup">pickup</option>
          <option value="delivery">delivery</option>
        </select>
        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm"
            data-testid="filter-from-date"
          />
          <label className="text-xs text-slate-500 ml-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm"
            data-testid="filter-to-date"
          />
        </div>
        <Button onClick={applyFilter} size="sm">Apply</Button>
        {hasFilters && (
          <Button variant="outline" size="sm" onClick={clearFilters}>Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.label}
                    className={`px-3 py-2 font-semibold ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.sortable ? 'cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700/60 select-none' : ''}`}
                    onClick={() => c.sortable && toggleSort(c.key)}
                    data-testid={c.sortable ? `sort-${c.key}` : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.sortable && sortKey === c.key && (
                        order === 'asc'
                          ? <ChevronUp className="w-3 h-3" />
                          : <ChevronDown className="w-3 h-3" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-slate-500">
                  <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-slate-500">No rows for this filter.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="px-3 py-2 font-mono text-slate-500">{r.id}</td>
                  <td className="px-3 py-2 font-mono">{r.device_uid || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{r.work_order_id ? r.work_order_id.slice(0, 8) + '…' : '—'}</td>
                  <td className="px-3 py-2 font-mono">{fmtTs(r.recorded_at)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.latitude, 6)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.longitude, 6)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.temperature, 3)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.humidity, 2)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.weight, 3)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.battery_level, 2)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(r.speed, 3)}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px]">{r.event_type}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    {r.bag_open ? <Badge className="bg-rose-500 text-white text-[10px]">OPEN</Badge> : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-500">{fmtTs(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 border-t border-slate-200 dark:border-slate-800 text-sm">
          <div className="text-slate-600 dark:text-slate-400">
            Page {page} of {lastPage} · {total.toLocaleString()} rows total · sort={sortKey} {order}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-slate-500">Per page</label>
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="h-8 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm"
            >
              {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage || loading}
              data-testid="next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
